/**
 * Выбор подразделения и доски. Обе части — в одной строке шапки:
 * селект подразделений и селект досок этого подразделения.
 *
 * §8.5: показываются только доступные подразделения, внутри — доступные
 * доски, выбор сохраняется между переходами. Прежде вкладки появлялись,
 * лишь когда их было больше одной, а название доски было простым
 * заголовком: с одной доской не было видно ни подразделений, ни способа
 * перейти в соседнее.
 *
 * Доски — селектом, а не рядом вкладок: у подразделения их бывает
 * десяток, и ряд вкладок отжимал строку шапки во вторую. Подразделения —
 * тоже селектом: с подчинёнными их стало больше, а вложенность в ряду
 * вкладок не видна. В списке подчинённые стоят под вышестоящим с отступом.
 *
 * Сразу за селектом — вход в список задач всех досок подразделения.
 * На этом списке выбор подразделения ведёт на такой же список, а не
 * на доску: переключая подразделение, пользователь не должен терять вид.
 * Повторное нажатие на иконку возвращает на доску, с которой пришли, —
 * открытую в подразделении последней.
 */

import { List } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { useAppSelector } from "@/app/hooks";
import { useDepartmentsQuery } from "@taskflow/client-core/directory";
import { cn, type PickerOption, Select } from "@/shared/ui";

import { useBoardsQuery } from "@taskflow/client-core/boards";
import { useDepartmentTabs } from "@taskflow/client-core/boards";
import { pickBoardFor } from "./lastBoard";

export function BoardSwitcher({
  departmentCode,
  boardId,
  view = "board",
}: {
  departmentCode: string;
  /** Открытая доска; null — подразделение без досок или открыт список. */
  boardId: string | null;
  /** Открыт список задач подразделения, а не доска. */
  view?: "board" | "list";
}) {
  const user = useAppSelector((state) => state.auth.user);
  const boards = useBoardsQuery();
  const departments = useDepartmentsQuery();
  // Порядок и подписи — из справочника: вышестоящее, за ним подчинённые.
  const tabs = useDepartmentTabs();
  const navigate = useNavigate();

  const available = boards.data?.data ?? [];

  // §2.3: доступные подразделения — основное и выданные пользователю;
  // администратору сервер выдаёт все. Подразделения, где у пользователя
  // уже есть доски, показываются в любом случае.
  const accessible = new Set<string>([
    ...(user === null ? [] : [user.primaryDepartmentId, ...user.allowedDepartmentIds]),
  ]);
  const codes = new Set<string>([departmentCode, ...available.map((board) => board.departmentCode)]);
  for (const department of departments.data?.data ?? []) {
    if (accessible.has(department.id)) codes.add(department.code);
  }
  const shownDepartments = tabs.order.filter((code) => codes.has(code));
  // Отступ — по видимым предкам: подчинённое недоступного подразделения
  // встаёт на верхний уровень, а не висит с отступом без родителя.
  const parentOf = new Map<string, string>();
  const codeOf = new Map((departments.data?.data ?? []).map((item) => [item.id, item.code]));
  for (const item of departments.data?.data ?? []) {
    const parent = item.parentId == null ? undefined : codeOf.get(item.parentId);
    if (parent !== undefined) parentOf.set(item.code, parent);
  }
  const departmentOptions: PickerOption[] = shownDepartments.map((code) => {
    let depth = 0;
    // Шаги ограничены числом подразделений — на случай петли в данных.
    let parent = parentOf.get(code);
    for (let step = 0; parent !== undefined && step <= parentOf.size; step += 1) {
      if (codes.has(parent)) depth += 1;
      parent = parentOf.get(parent);
    }
    return { id: code, label: tabs.labels[code] ?? code, depth };
  });
  const departmentPath = (code: string) => {
    if (view === "list") return departmentListPath(code);
    const target = pickBoardFor(code, available);
    return target === null ? `/tasks?department=${code}` : `/boards/${target}`;
  };
  const siblings = available.filter((board) => board.departmentCode === departmentCode);
  // Со списка иконка ведёт обратно на доску, а не на тот же список.
  const backBoard =
    view === "list"
      ? siblings.find((board) => board.id === pickBoardFor(departmentCode, available))
      : undefined;
  const listLabel =
    backBoard === undefined
      ? "Все задачи подразделения списком"
      : `Вернуться на доску «${backBoard.name}»`;

  return (
    // На телефоне — одна строка: селект доски сжимается, а не переносит
    // кнопку списка на отдельный ряд (docs/08-mobile-roadmap.md, M3).
    <div className="flex min-w-0 flex-wrap items-center gap-2 max-sm:w-full max-sm:flex-nowrap">
      <div className="w-44 shrink-0 max-sm:w-auto max-sm:min-w-0 max-sm:flex-1 max-sm:shrink">
        <Select
          label="Подразделение"
          placeholder="Выберите подразделение"
          options={departmentOptions}
          value={departmentCode}
          onChange={(code) => void navigate(departmentPath(code))}
        />
      </div>

      {siblings.length > 0 && (
        <div className="w-52 shrink-0 max-sm:w-auto max-sm:min-w-0 max-sm:flex-1 max-sm:shrink">
          <Select
            label="Доска подразделения"
            placeholder="Выберите доску"
            options={siblings.map((board) => ({ id: board.id, label: board.name }))}
            value={boardId}
            onChange={(id) => void navigate(`/boards/${id}`)}
          />
        </div>
      )}

      {siblings.length > 0 && (
        <Link
          to={
            backBoard === undefined ? departmentListPath(departmentCode) : `/boards/${backBoard.id}`
          }
          aria-label={listLabel}
          title={listLabel}
          className={cn(
            "inline-flex size-8 shrink-0 items-center justify-center rounded-md border transition-colors",
            view === "list"
              ? "border-accent bg-accent-soft text-accent"
              : "border-line bg-surface text-fg hover:bg-sunken",
          )}
        >
          <List className="size-4" aria-hidden />
        </Link>
      )}
    </div>
  );
}

/** Список задач всех досок подразделения. */
function departmentListPath(code: string): string {
  return `/tasks/list?department=${code}`;
}
